import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import {
  type Blockchain,
  type CancelByOrderRequest,
  type OrderId,
  type ReatomEvmContainer,
  cancelByOrderFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { CancelByOrderToast } from "../../toasts/cancel-by-order"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

export interface CancelByOrderActionParams {
  orderId: OrderId
  blockchain: Blockchain
}

export interface CancelByOrderActionResult {
  transactionId: string
  orderId: OrderId
  blockchain: Blockchain
}

export const CancelByOrderTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)

  const cancelByOrderActionFactory = () => {
    const cancelByOrderAction = action(
      async ({ orderId, blockchain }: CancelByOrderActionParams) => {
        const container = sdkContainerAtom()

        // Execute cancel by order via Reatom SDK
        const cancelByOrder = cancelByOrderFactoryReatom(container)
        const request: CancelByOrderRequest = { orderId, blockchain }

        const transactionId = await wrap(cancelByOrder(request))

        // Show success toast in mutationFn (as in original)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CancelByOrderToast.Success
            id={id}
            transactionId={transactionId}
            blockchain={blockchain}
          />
        ))

        return { transactionId, orderId, blockchain }
      },
      "cancelByOrderAction",
    ).extend(
      withAsync(),

      // onCall: Called when action starts (replaces onMutate)
      withCallHook(async (values, [params]) => {
        const { orderId } = params

        sonnerToast.custom((id) => <CancelByOrderToast.Pending id={id} />, {
          duration: Number.POSITIVE_INFINITY,
        })

        const baseTransactionEventContext: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
        }

        logger?.api.pushEvent(
          TransactionEvent.Started,
          baseTransactionEventContext,
        )
        amplitude.track(TransactionEvent.Started, baseTransactionEventContext)
      }),
    )

    // onFulfill: Called on success (replaces onSuccess)
    cancelByOrderAction.onFulfill.extend(
      withCallHook((result, [params]) => {
        const { transactionId, orderId } = result.payload

        const baseTransactionEventContext: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
        }

        const context = {
          ...baseTransactionEventContext,
        }

        if (transactionId) {
          context.transactionId = transactionId
        }

        logger?.api.pushEvent(TransactionEvent.Completed, context)
        amplitude.track(TransactionEvent.Completed, context)
      }),
    )

    // onReject: Called on error (replaces onError)
    cancelByOrderAction.onReject.extend(
      withCallHook(({ error, params: [paramsValue] }) => {
        console.error("Cancel by order failed:", error)

        const { orderId } = paramsValue

        const baseTransactionEventContext: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
        }

        const context = {
          ...baseTransactionEventContext,
          error: getTransactionErrorMessage(error),
        }

        logger?.api.pushEvent(TransactionEvent.Failed, context)
        amplitude.track(TransactionEvent.Failed, context)

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CancelByOrderToast.Error id={id} error={error as Error} />
        ))
      }),
    )

    return cancelByOrderAction
  }

  return { cancelByOrderActionFactory }
})
